/**
 * @file src/App.jsx
 * @description Root application component with routing and layout
 * 
 * Sets up the core React app structure:
 * - Authentication provider (wraps entire app)
 * - Routes for all pages
 * - Header and Footer layout
 * - Page transition animations
 * - Protected route guards
 * - Admin-only route checks
 * 
 * Route Structure:
 *   / - Home landing page (public)
 *   /about, /faq, /privacy - Info pages (public)
 *   /login, /signup - Auth pages (public)
 *   /capsuleList - Dashboard (protected)
 *   /capsule/:id - View/edit capsule (protected)
 *   /capsule/:id/wizard/:templateType - Create with wizard (protected)
 *   /viewer/:viewToken - Public capsule viewer (no auth required)
 *   /contacts - Manage contacts (protected)
 *   /profile - User settings (protected)
 *   /admin - Analytics dashboard (admin only)
 * 
 * @dependencies
 *   - React Router v6 (client-side routing)
 *   - Framer Motion (page animations)
 *   - AuthContext (user session)
 * 
 * @exports
 *   - App (default)
 */

// src/App.jsx
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import { FooterAd } from './components/AdSlot';
import PageTransition from './components/PageTransition';
import ProtectedRoute from './components/ProtectedRoute';
import { ADMIN_EMAIL } from './config/admin';

// Lazy-loaded pages — each becomes its own chunk
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const CapsuleList = lazy(() => import('./pages/CapsuleList'));
const Contacts = lazy(() => import('./pages/Contacts'));
const NewsletterCapture = lazy(() => import('./components/NewsletterCapture'));
const CapsuleDetail = lazy(() => import('./pages/CapsuleDetail'));
const CapsuleWizard = lazy(() => import('./pages/CapsuleWizard'));
const FamilyTreeBuilder = lazy(() => import('./pages/FamilyTreeBuilder'));
const SuccessPage = lazy(() => import('./components/SuccessPage'));
const CancelPage = lazy(() => import('./components/CancelPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const CapsuleViewer = lazy(() => import('./pages/CapsuleViewer'));
const DelayDelivery = lazy(() => import('./pages/DelayDelivery'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Login = lazy(() => import('./pages/Login'));
const Profile = lazy(() => import('./pages/Profile'));
const Integrations = lazy(() => import('./pages/Integrations'));
const Admin = lazy(() => import('./pages/Admin'));
const HandleEmailAction = lazy(() => import('./pages/HandleEmailAction'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const ForParents = lazy(() => import('./pages/ForParents'));
const ForEstatePlanning = lazy(() => import('./pages/ForEstatePlanning'));
const Templates = lazy(() => import('./pages/Templates'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const DemoBanner = lazy(() => import('./components/DemoBanner'));

// Scroll to top whenever the route changes
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Move focus to main content for screen readers on route change
    const main = document.getElementById('main');
    if (main) { main.tabIndex = -1; main.focus({ preventScroll: true }); }
  }, [pathname]);
  return null;
}

function AdminRoute({ children }) {
    const { user } = useAuth();
    if (user?.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;
    return children;
}

const GA_ID = 'G-Q5S4CKCYXM';

function AppRoutes() {
    const { loading, user } = useAuth();
    const location = useLocation();

    // Fire a GA4 page_view on every route change
    useEffect(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('config', GA_ID, { page_path: location.pathname + location.search });
        }
    }, [location]);

    // Show email verification banner for unverified email/password users
    const isEmailUser = user && user.providerData?.[0]?.providerId === 'password';
    const showVerifyBanner = isEmailUser && !user.emailVerified && location.pathname !== '/verify-email';

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-vault-900">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-vault-400 text-sm">Loading...</p>
                </div>
            </div>
        );
    }

    // Check if this is a demo route (isolated from main app)
    const isDemoRoute = location.pathname.startsWith('/demo');

    return (
        <div className="flex flex-col min-h-screen">
            <ScrollToTop />
            {!isDemoRoute && <Header />}
            {!isDemoRoute && <Suspense fallback={null}><DemoBanner /></Suspense>}
            {!isDemoRoute && showVerifyBanner && (
                <div role="alert" aria-live="polite" className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-4">
                    <p className="text-xs text-amber-800 font-medium">
                        Please verify your email address — check your inbox at <span className="font-semibold">{user.email}</span>.
                    </p>
                    <a href="/verify-email" className="text-xs font-semibold text-amber-700 underline hover:text-amber-900 flex-shrink-0">Resend / verify →</a>
                </div>
            )}
            <main id="main" role="main" className={`flex-1${user && !isDemoRoute ? ' pb-16 md:pb-0' : ''}`}>
              <Suspense fallback={
                <div className="flex items-center justify-center min-h-[50vh]">
                  <div className="w-8 h-8 border-3 border-accent-500 border-t-transparent rounded-full animate-spin" />
                </div>
              }>
                <AnimatePresence mode="wait">
                    <Routes location={location} key={location.pathname}>
                        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
                        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
                        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
                        <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
                        <Route path="/for-parents" element={<PageTransition><ForParents /></PageTransition>} />
                        <Route path="/for-estate-planning" element={<PageTransition><ForEstatePlanning /></PageTransition>} />
                        <Route path="/templates" element={<PageTransition><Templates /></PageTransition>} />
                        <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
                        <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
                        <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />
                        <Route path="/signup" element={user ? <Navigate to="/" replace /> : <PageTransition><SignUp /></PageTransition>} />
                        <Route path="/login" element={user ? <Navigate to="/" replace /> : <PageTransition><Login /></PageTransition>} />
                        <Route path="/capsuleList" element={<ProtectedRoute><PageTransition><CapsuleList /></PageTransition></ProtectedRoute>} />
                        <Route path="/capsule/:id" element={<ProtectedRoute><PageTransition><CapsuleDetail /></PageTransition></ProtectedRoute>} />
                        <Route path="/capsule/:id/wizard" element={<ProtectedRoute><CapsuleWizard /></ProtectedRoute>} />
                        <Route path="/capsule/:id/family-tree" element={<ProtectedRoute><FamilyTreeBuilder /></ProtectedRoute>} />
                        <Route path="/contacts" element={<ProtectedRoute><PageTransition><Contacts /></PageTransition></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><PageTransition><Profile /></PageTransition></ProtectedRoute>} />
                        <Route path="/integrations" element={<ProtectedRoute><PageTransition><Integrations /></PageTransition></ProtectedRoute>} />
                        <Route path="/success" element={<ProtectedRoute><PageTransition><SuccessPage /></PageTransition></ProtectedRoute>} />
                        <Route path="/cancel" element={<PageTransition><CancelPage /></PageTransition>} />
                        <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
                        <Route path="/tos" element={<PageTransition><TermsOfService /></PageTransition>} />
                        <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
                        <Route path="/auth-action" element={<HandleEmailAction />} />
                        <Route path="/accept-invite" element={<PageTransition><AcceptInvite /></PageTransition>} />
                        <Route path="/view/:viewToken" element={<CapsuleViewer />} />
                        <Route path="/delay-delivery" element={<PageTransition><DelayDelivery /></PageTransition>} />
                        <Route path="/admin" element={<ProtectedRoute><AdminRoute><PageTransition><Admin /></PageTransition></AdminRoute></ProtectedRoute>} />
                        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
                    </Routes>
                </AnimatePresence>
              </Suspense>
            </main>
            {!isDemoRoute && <FooterAd />}
            {!isDemoRoute && <Footer />}
            {!isDemoRoute && <BottomNav />}
            {!isDemoRoute && <Suspense fallback={null}><NewsletterCapture /></Suspense>}
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppRoutes />
            </Router>
        </AuthProvider>
    );
}

export default App;
